import React, { useMemo, useState } from "react";
import type { IEmailBuilderMediaItem } from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilderTypes";
import { useAppSelector } from "hooks/redux";
import {
  imageBlockPatchFromEmailBuilderMediaItem,
  resolveMediaRefMediaId,
} from "../../operations/emailBuilderMediaRef";
import { emailBuilderFacilityProfile } from "../../operations/emailBuilderFacilityProfile";
import { imageBlockPatchForMediaChange } from "../../operations/imageBlockMediaChange";
import { EmailBuilderMediaGalleryModal } from "../../shell/panel/blockSettings/EmailBuilderMediaGalleryModal";
import { resolveBlockLayoutContext } from "../../domain/block-width";
import { findColumn, findRow } from "../../domain/find";
import { canUseFillColumn } from "../../domain/image-height";
import { resolveMediaRef } from "../../domain/media";
import { selectDocument, selectEmailSettings } from "../../domain/selectors";
import { useEditorStore } from "../../domain/redux/hooks";
import type { ImageBlock } from "../../domain/types";
import { EmailBuilderSettingsImagePicker } from "./EmailBuilderSettingsImagePicker";

type ImageBlockMediaFieldProps = {
  block: ImageBlock;
  patch: (next: Partial<ImageBlock>) => void;
  rowId?: string;
  columnId?: string;
};

export function ImageBlockMediaField({
  block,
  patch,
  rowId,
  columnId,
}: ImageBlockMediaFieldProps) {
  const adminFacility = useAppSelector(store => store.facilityStore.facility);
  const facility = useMemo(
    () => emailBuilderFacilityProfile(adminFacility),
    [adminFacility],
  );
  const document = useEditorStore(selectDocument);
  const settings = useEditorStore(selectEmailSettings);
  const [mediaOpen, setMediaOpen] = useState(false);
  const previewUrl = resolveMediaRef(block.media, facility);
  const hasImage = block.media != null && previewUrl.trim().length > 0;
  const { contentMaxWidth } =
    rowId && columnId
      ? resolveBlockLayoutContext(document, rowId, columnId, block.padding)
      : { contentMaxWidth: 0 };

  function handleConfirmMedia(result: {
    media: IEmailBuilderMediaItem;
    alt?: string;
  }) {
    const layoutWidth =
      contentMaxWidth > 0 ? contentMaxWidth : settings.contentWidth;
    const row = rowId ? findRow(document, rowId) : undefined;
    const column =
      rowId && columnId ? findColumn(document, rowId, columnId) : undefined;
    patch(
      imageBlockPatchForMediaChange({
        block,
        action: block.media ? "replace" : "add",
        contentMaxWidth: layoutWidth,
        fillColumnAllowed: canUseFillColumn(row, column, block),
        mediaPatch: imageBlockPatchFromEmailBuilderMediaItem(result.media),
        alt: result.alt,
      }),
    );
  }

  return (
    <>
      <EmailBuilderSettingsImagePicker
        previewUrl={hasImage ? previewUrl : undefined}
        onOpen={() => setMediaOpen(true)}
        onRemove={() => {
          patch(
            imageBlockPatchForMediaChange({
              block,
              action: "remove",
              contentMaxWidth:
                contentMaxWidth > 0 ? contentMaxWidth : settings.contentWidth,
            }),
          );
        }}
      />

      {mediaOpen ? (
        <EmailBuilderMediaGalleryModal
          mode={block.media ? "replace" : "add"}
          isOpen
          initialAlt={block.media ? block.alt : undefined}
          initialMediaId={resolveMediaRefMediaId(block.media)}
          includeFacilityLogo
          linkedImageHint={block.href.trim().length > 0}
          onClose={() => setMediaOpen(false)}
          onConfirm={handleConfirmMedia}
        />
      ) : null}
    </>
  );
}
